import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { uploadImageToS3, deleteImageFromS3, getFileExtension } from "../../utils/s3Upload";
import { getRole } from "../../utils/role";

const FollowUpStage4 = ({ reportId, initialData = null, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState({});

  const [formData, setFormData] = useState({
    current_yield_quantity: "",
    rate_previous_season_rs: "",
    rate_current_season_rs: "",
    
    input_costs: [
        { name: "", quantity: "", unit: "", price_per_unit: "", total_cost: "", is_fixed: false }
    ],
    input_cost_receipt_urls: [], 
    remarks: ""
  });

  const isReadOnly = Boolean(initialData && Object.keys(initialData).length > 0) && getRole() !== 'SUPPORT';

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const sanitizedData = { ...initialData };
      if (typeof sanitizedData.input_cost_receipt_url === "string") {
        sanitizedData.input_cost_receipt_urls = [sanitizedData.input_cost_receipt_url];
      } else if (!Array.isArray(sanitizedData.input_cost_receipt_urls)) {
        sanitizedData.input_cost_receipt_urls = [];
      }
      setFormData(sanitizedData);
    }
    setLoading(false);
  }, [initialData]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTableChange = (index, field, value) => {
    if (isReadOnly) return;
    const updatedTable = formData.input_costs.map((row, i) => {
      if (i !== index) return row;
      const updatedRow = { ...row, [field]: value };
      if (field === "quantity" || field === "price_per_unit") {
        const qty = parseFloat(field === "quantity" ? value : updatedRow.quantity) || 0;
        const price = parseFloat(field === "price_per_unit" ? value : updatedRow.price_per_unit) || 0;
        updatedRow.total_cost = qty > 0 && price > 0 ? (qty * price).toFixed(2) : "";
      }
      return updatedRow;
    });
    handleChange("input_costs", updatedTable);
  };

  const addTableRow = () => {
    handleChange("input_costs", [
      ...formData.input_costs,
      { name: "", quantity: "", unit: "", price_per_unit: "", total_cost: "", is_fixed: false }
    ]);
  };

  const removeTableRow = (index) => {
    handleChange("input_costs", formData.input_costs.filter((_, i) => i !== index));
  };

  const handleMultiPhotoUpload = async (fieldKey, fileList) => {
    if (isReadOnly || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      const uploadPromises = files.map(async (file) => {
        const fileExt = getFileExtension(file);
        return await uploadImageToS3(file, fileExt, "follow-up-photos");
      });
      const newS3Urls = await Promise.all(uploadPromises);
      const existingUrls = formData[fieldKey] || [];
      handleChange(fieldKey, [...existingUrls, ...newS3Urls]);
    } catch (err) {
      alert("Failed to upload photos to AWS S3.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handlePhotoRemove = async (fieldKey, indexToRemove) => {
    if (isReadOnly) return;
    const urlsArray = formData[fieldKey] || [];
    const fileUrl = urlsArray[indexToRemove];
    if (!fileUrl) return;
    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      await deleteImageFromS3(fileUrl);
      const updatedUrls = urlsArray.filter((_, i) => i !== indexToRemove);
      handleChange(fieldKey, updatedUrls);
    } catch (err) {
      alert("Could not delete photo from AWS server.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (Object.values(uploadingMedia).some((up) => up)) {
      alert("Please wait for all photo uploads to finish before submitting.");
      return;
    }

    const hasValidCostRow = formData.input_costs.some(row => parseFloat(row.total_cost) > 0);
    if (!hasValidCostRow) {
      alert("Input Cost Breakdown is required. Please fill in the quantity and price for Fertilizer, Pesticide, or another input.");
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.post(`/follow-up/response/${reportId}`, {
        follow_up_stage: 4,
        answers: formData
      });
      alert("Stage 4 Final Harvest Outcomes submitted successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit Stage 4.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500 text-sm">Loading Stage 4 form...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-left">

      {isReadOnly && (
        <div className="bg-blue-50 text-blue-800 text-xs sm:text-sm p-3 rounded-md border border-blue-200 font-medium flex items-center gap-2">
          🔒 This form has already been submitted and is locked for editing.
        </div>
      )}
      
      {/* SECTION 1: TOTAL CURRENT YIELD */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">1. Harvest Yield Outcome</h4>
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
            1. Total Current Yield {!isReadOnly && <span className="text-red-500">*</span>}
          </label>
          <div className="relative max-w-md">
            <input
              type="number"
              step="any"
              value={formData.current_yield_quantity}
              onChange={(e) => handleChange("current_yield_quantity", e.target.value)}
              required
              disabled={isReadOnly}
              placeholder="Enter total quantity harvested..."
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 pr-24 text-sm focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100 disabled:text-gray-600"
            />
            <span className="absolute right-0 inset-y-0 flex items-center px-3 bg-gray-100 border-l border-gray-300 text-xs font-semibold text-gray-600 rounded-r-md">
              Quintals
            </span>
          </div>
        </div>
      </div>

      {/* SECTION 2: MARKET RATE COMPARISON */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">2. Market Rate Comparison (₹ per Quintal)</h4>
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            2. Selling Price / Market Rate Comparison {!isReadOnly && <span className="text-red-500">*</span>}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
              <span className="block text-xs font-semibold text-gray-600 mb-1">Previous Season Rate (₹ per Quintal)</span>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={formData.rate_previous_season_rs}
                  onChange={(e) => handleChange("rate_previous_season_rs", e.target.value)}
                  required
                  disabled={isReadOnly}
                  placeholder="e.g., 2150"
                  className="w-full border border-gray-300 rounded-md p-2 pl-7 text-sm bg-white focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-600"
                />
              </div>
            </div>
            <div className="bg-purple-50/50 p-3 rounded-md border border-purple-200">
              <span className="block text-xs font-semibold text-purple-900 mb-1">Current Season Rate (₹ per Quintal)</span>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={formData.rate_current_season_rs}
                  onChange={(e) => handleChange("rate_current_season_rs", e.target.value)}
                  required
                  disabled={isReadOnly}
                  placeholder="e.g., 2300"
                  className="w-full border border-purple-300 rounded-md p-2 pl-7 text-sm bg-white font-semibold text-purple-950 focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-600"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: TOTAL ADVISORY-PERIOD INVESTMENT */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">3. Total Advisory-Period Investment Breakdown</h4>

        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <table className="w-full text-left border-collapse min-w-[550px]">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300 text-[11px] sm:text-xs uppercase text-gray-700">
                <th className="p-2">Name of Input</th>
                <th className="p-2 w-20 sm:w-24">Quantity</th>
                <th className="p-2 w-20 sm:w-24">Unit</th>
                <th className="p-2 w-24 sm:w-28">Price/Unit (₹)</th>
                <th className="p-2 w-24 sm:w-28">Total Cost (₹)</th>
                <th className="p-2 text-center w-10 sm:w-12">Del</th>
              </tr>
            </thead>
            <tbody>
              {formData.input_costs.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-1 sm:p-1.5">
                    <input type="text" value={row.name} readOnly={row.is_fixed} onChange={(e) => handleTableChange(idx, "name", e.target.value)} disabled={isReadOnly} placeholder="e.g., Urea" className={`w-full border rounded p-1.5 text-xs sm:text-sm ${row.is_fixed ? "bg-gray-100 font-semibold cursor-not-allowed" : ""}`} />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input type="number" step="any" value={row.quantity} onChange={(e) => handleTableChange(idx, "quantity", e.target.value)} disabled={isReadOnly} placeholder="0" className="w-full border rounded p-1.5 text-xs sm:text-sm" />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input type="text" value={row.unit} readOnly={row.is_fixed} onChange={(e) => handleTableChange(idx, "unit", e.target.value)} disabled={isReadOnly} placeholder="Bag/kg" className={`w-full border rounded p-1.5 text-xs sm:text-sm ${row.is_fixed ? "bg-gray-100 font-semibold cursor-not-allowed" : ""}`} />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input type="number" step="any" value={row.price_per_unit} onChange={(e) => handleTableChange(idx, "price_per_unit", e.target.value)} disabled={isReadOnly} placeholder="0.00" className="w-full border rounded p-1.5 text-xs sm:text-sm" />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input type="number" step="any" value={row.total_cost} readOnly placeholder="Auto" className="w-full border border-purple-200 bg-purple-50/30 rounded p-1.5 text-xs sm:text-sm font-semibold text-purple-900" />
                  </td>
                  <td className="p-1 sm:p-1.5 text-center">
                    {!row.is_fixed ? (
                      <button type="button" onClick={() => removeTableRow(idx)} disabled={isReadOnly} className="text-red-500 hover:text-red-700 font-bold px-1.5 py-0.5 rounded hover:bg-red-50 text-sm">✕</button>
                    ) : (
                      <span className="text-gray-300 text-xs" title="Required input">🔒</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={addTableRow} disabled={isReadOnly} className="mt-3 w-full sm:w-auto text-xs font-semibold bg-purple-50 text-purple-700 px-3 py-2 sm:py-1.5 rounded-md border border-purple-200 hover:bg-purple-100 transition">+ Add Custom Input / Expense</button>
        </div>

        <div className="bg-purple-50/40 border border-dashed border-purple-200 p-3 sm:p-4 rounded-md mt-4">
          <label className="block text-xs font-semibold text-purple-900 mb-1.5">📷 Optional: Upload Bill or Receipt Photos (Multiple allowed)</label>
          <input type="file" multiple accept="image/*,.pdf" disabled={uploadingMedia.input_cost_receipt_urls || isReadOnly} onChange={(e) => handleMultiPhotoUpload("input_cost_receipt_urls", e.target.files)} className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 file:font-semibold hover:file:bg-purple-200 cursor-pointer disabled:opacity-50" />
          {uploadingMedia.input_cost_receipt_urls && <div className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-2"><span className="inline-block w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>Uploading photos...</div>}
          
          {formData.input_cost_receipt_urls?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2.5">
              {formData.input_cost_receipt_urls.map((url, idx) => (
                <div key={idx} className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-md border border-purple-200 text-xs shadow-sm max-w-full">
                  <span className="text-green-600 font-bold shrink-0">✓</span>
                  <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 underline truncate max-w-[140px] sm:max-w-[200px]">{url.split("/").pop()}</a>
                  {!isReadOnly && <button type="button" onClick={() => handlePhotoRemove("input_cost_receipt_urls", idx)} className="text-red-500 hover:text-red-700 font-bold ml-1 p-0.5 shrink-0">✕</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SECTION 4: REMARKS */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">General Remarks / Additional Field Notes</label>
        <textarea rows={3} value={formData.remarks} onChange={(e) => handleChange("remarks", e.target.value)} disabled={isReadOnly} placeholder="Add any general observations, farmer satisfaction feedback, or summary harvest remarks..." className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100 disabled:text-gray-600" />
      </div>

      {!isReadOnly && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button type="submit" disabled={submitting || Object.values(uploadingMedia).some((up) => up)} className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition shadow-sm">
            {submitting ? "Saving Stage 4..." : initialData ? "Update Stage 4" : "Submit Stage 4"}
          </button>
        </div>
      )}
    </form>
  );
};

export default FollowUpStage4;